import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import crmService from '../../services/crmService';

const STATUSES = ['new', 'contacted', 'qualified', 'won', 'lost', 'archived'];

const STATUS_STYLES = {
  new: 'bg-blue-500/20 text-blue-300',
  contacted: 'bg-amber-500/20 text-amber-300',
  qualified: 'bg-purple-500/20 text-purple-300',
  won: 'bg-green-500/20 text-green-300',
  lost: 'bg-red-500/20 text-red-300',
  archived: 'bg-gray-500/20 text-gray-300',
};

const digits = (p) => (p || '').replace(/[^\d+]/g, '');
const fmtDate = (s) => (s ? new Date(s).toLocaleString() : '—');

function StatCard({ label, value, accent }) {
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
      <div className="text-xs uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${accent || 'text-white'}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[status] || STATUS_STYLES.new}`}>
      {status}
    </span>
  );
}

export default function LeadsPage() {
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [filters, setFilters] = useState({ status: '', source: '', q: '' });
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link: open a lead's drawer when arriving via /leads?contact=<id>
  // (e.g. from a "New enquiry" notification).
  useEffect(() => {
    const cid = searchParams.get('contact');
    if (cid) setSelectedId(Number(cid));
  }, [searchParams]);

  const closeDrawer = () => {
    setSelectedId(null);
    if (searchParams.get('contact')) {
      searchParams.delete('contact');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const loadStats = useCallback(async () => {
    try { setStats(await crmService.analytics(90)); } catch (e) { /* noop */ }
  }, []);

  const loadContacts = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const data = await crmService.listContacts({ ...filters, page, pageSize: 25 });
      setContacts(data.contacts || []);
      setPagination(data.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch (e) {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadContacts(1); }, [loadContacts]);

  const onFilter = (patch) => setFilters((f) => ({ ...f, ...patch }));

  const refresh = () => { loadContacts(pagination.page); loadStats(); };

  return (
    <div className="p-6 text-gray-200">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Leads</h1>
          <p className="text-gray-400 text-sm">Contacts captured from your widget & chat — reach out and track status.</p>
        </div>
        <button
          onClick={() => crmService.downloadCsv({ status: filters.status, source: filters.source })}
          className="px-3 py-2 text-sm rounded-md bg-gray-700 hover:bg-gray-600 text-white"
        >
          Export CSV
        </button>
      </div>

      {/* Analytics */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatCard label="Total leads" value={stats.total} />
          <StatCard label="New" value={stats.byStatus?.new || 0} accent="text-blue-300" />
          <StatCard label="Qualified" value={stats.byStatus?.qualified || 0} accent="text-purple-300" />
          <StatCard label="Won" value={stats.byStatus?.won || 0} accent="text-green-300" />
          <StatCard label="Conversion" value={`${stats.funnel?.conversionRate ?? 0}%`} accent="text-emerald-300" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <input
          value={filters.q}
          onChange={(e) => onFilter({ q: e.target.value })}
          placeholder="Search name, email, phone…"
          className="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={filters.status}
          onChange={(e) => onFilter({ status: e.target.value })}
          className="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={filters.source}
          onChange={(e) => onFilter({ source: e.target.value })}
          className="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm"
        >
          <option value="">All sources</option>
          <option value="widget_registration">Widget registration</option>
          <option value="chat_lead">Chat lead</option>
          <option value="enquiry">Enquiry</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-900/60 text-gray-400 text-left">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Last activity</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
            )}
            {!loading && contacts.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No leads yet.</td></tr>
            )}
            {!loading && contacts.map((c) => (
              <tr
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                className="border-t border-gray-700 hover:bg-gray-700/40 cursor-pointer"
              >
                <td className="px-4 py-3 text-white">{c.fullName || '—'}</td>
                <td className="px-4 py-3">{c.email || '—'}</td>
                <td className="px-4 py-3">{c.phone || '—'}</td>
                <td className="px-4 py-3 text-gray-400">{c.source}</td>
                <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                <td className="px-4 py-3 text-gray-400">{fmtDate(c.lastActivityAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3 text-sm text-gray-400">
        <span>{pagination.total} lead{pagination.total === 1 ? '' : 's'}</span>
        <div className="flex gap-2">
          <button
            disabled={pagination.page <= 1}
            onClick={() => loadContacts(pagination.page - 1)}
            className="px-2 py-1 rounded bg-gray-700 disabled:opacity-40"
          >Prev</button>
          <span>Page {pagination.page} / {pagination.totalPages}</span>
          <button
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => loadContacts(pagination.page + 1)}
            className="px-2 py-1 rounded bg-gray-700 disabled:opacity-40"
          >Next</button>
        </div>
      </div>

      {selectedId && (
        <LeadDrawer
          contactId={selectedId}
          onClose={closeDrawer}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

function LeadDrawer({ contactId, onClose, onChanged }) {
  const [contact, setContact] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setContact(await crmService.getContact(contactId)); } catch (e) { onClose(); }
  }, [contactId, onClose]);

  useEffect(() => { load(); }, [load]);

  if (!contact) {
    return (
      <div className="fixed inset-0 z-40 flex justify-end bg-black/50" onClick={onClose}>
        <div className="w-full max-w-lg bg-gray-900 p-6 text-gray-400">Loading…</div>
      </div>
    );
  }

  const changeStatus = async (status) => {
    setBusy(true);
    try { await crmService.updateContact(contact.id, { status }); await load(); onChanged?.(); }
    finally { setBusy(false); }
  };

  const saveNotes = async (notes) => {
    setBusy(true);
    try { await crmService.updateContact(contact.id, { notes }); await load(); }
    finally { setBusy(false); }
  };

  const logAndOpen = async (type, href) => {
    if (href) window.open(href, '_blank');
    try { await crmService.logActivity(contact.id, type, `${type} initiated`); await load(); onChanged?.(); } catch (e) {}
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/50" onClick={onClose}>
      <div className="w-full max-w-lg h-full bg-gray-900 border-l border-gray-700 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-gray-800">
          <div>
            <h2 className="text-xl font-bold text-white">{contact.fullName || 'Unnamed lead'}</h2>
            <div className="mt-1 flex items-center gap-2"><StatusBadge status={contact.status} /><span className="text-xs text-gray-500">{contact.source}</span></div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl leading-none">×</button>
        </div>

        <div className="p-5 space-y-5">
          {/* Contact fields */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Email" value={contact.email} />
            <Field label="Phone" value={contact.phone} />
            <Field label="First seen" value={fmtDate(contact.firstSeenAt)} />
            <Field label="Last contacted" value={fmtDate(contact.lastContactedAt)} />
          </div>

          {/* Sessions */}
          {contact.sessions?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                Sessions ({contact.sessions.length}) ·{' '}
                {contact.sessions.reduce((n, s) => n + (s.messageCount || 0), 0)} messages
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {contact.sessions.map((s) => (
                  <div key={s.sessionId} className="text-sm flex items-center justify-between border border-gray-800 rounded-md px-3 py-2">
                    <div>
                      <div className="text-gray-300">{s.messageCount} msg{s.messageCount === 1 ? '' : 's'}</div>
                      <div className="text-xs text-gray-500">last active {fmtDate(s.lastSeenAt)}</div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded ${s.status === 'active' ? 'bg-green-500/20 text-green-300' : 'bg-gray-500/20 text-gray-400'}`}>
                      {s.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Outreach */}
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Reach out</div>
            <div className="flex flex-wrap gap-2">
              <button disabled={!contact.email} onClick={() => setEmailOpen(true)}
                className="px-3 py-2 text-sm rounded-md bg-blue-600 hover:bg-blue-500 disabled:opacity-40">✉ Email</button>
              <button disabled={!contact.phone} onClick={() => logAndOpen('call', `tel:${digits(contact.phone)}`)}
                className="px-3 py-2 text-sm rounded-md bg-gray-700 hover:bg-gray-600 disabled:opacity-40">📞 Call</button>
              <button disabled={!contact.phone} onClick={() => logAndOpen('whatsapp', `https://wa.me/${digits(contact.phone).replace('+', '')}`)}
                className="px-3 py-2 text-sm rounded-md bg-green-600 hover:bg-green-500 disabled:opacity-40">🟢 WhatsApp</button>
            </div>
          </div>

          {/* Status */}
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Status</div>
            <select value={contact.status} disabled={busy} onChange={(e) => changeStatus(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Notes */}
          <NotesEditor initial={contact.notes || ''} onSave={saveNotes} busy={busy} />

          {/* Transcript */}
          {contact.transcript?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Conversation</div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {contact.transcript.map((t, i) => (
                  <div key={i} className="text-sm">
                    <div className="text-blue-300">Q: {t.question}</div>
                    <div className="text-gray-300">A: {t.answer}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Activity timeline */}
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Activity</div>
            <div className="space-y-2">
              {contact.activities?.length === 0 && <div className="text-sm text-gray-500">No activity yet.</div>}
              {contact.activities?.map((a) => (
                <div key={a.id} className="text-sm border-l-2 border-gray-700 pl-3">
                  <div className="text-gray-300"><span className="text-gray-500">[{a.type}]</span> {a.body}</div>
                  <div className="text-xs text-gray-600">{a.createdBy || 'system'} · {fmtDate(a.createdAt)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {emailOpen && (
          <EmailModal contact={contact} onClose={() => setEmailOpen(false)} onSent={() => { setEmailOpen(false); load(); onChanged?.(); }} />
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-gray-200 break-all">{value || '—'}</div>
    </div>
  );
}

function NotesEditor({ initial, onSave, busy }) {
  const [val, setVal] = useState(initial);
  useEffect(() => setVal(initial), [initial]);
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Notes</div>
      <textarea value={val} onChange={(e) => setVal(e.target.value)} rows={3}
        className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
      <button disabled={busy || val === initial} onClick={() => onSave(val)}
        className="mt-2 px-3 py-1.5 text-sm rounded-md bg-gray-700 hover:bg-gray-600 disabled:opacity-40">Save notes</button>
    </div>
  );
}

function EmailModal({ contact, onClose, onSent }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState(`Hi ${contact.fullName || 'there'},\n\n`);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    setSending(true);
    try {
      const res = await crmService.sendEmail(contact.id, subject, body);
      setResult(res);
      if (res.ok || res.delivered) setTimeout(onSent, 900);
    } catch (e) {
      setResult({ ok: false, error: e.response?.data?.detail || 'Failed to send' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="w-full max-w-md bg-gray-900 border border-gray-700 rounded-lg p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-white mb-1">Email {contact.fullName || contact.email}</h3>
        <p className="text-xs text-gray-500 mb-3">To: {contact.email}</p>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject"
          className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6}
          className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        {result && (
          <div className={`mt-2 text-sm ${result.ok || result.delivered ? 'text-green-400' : 'text-amber-400'}`}>
            {result.ok || result.delivered ? 'Sent ✓' :
              (result.emailEnabled === false ? 'Logged, but email sending is not configured (SMTP disabled).' : (result.error || 'Not delivered'))}
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm rounded-md bg-gray-700 hover:bg-gray-600">Cancel</button>
          <button disabled={sending || !subject} onClick={send}
            className="px-3 py-2 text-sm rounded-md bg-blue-600 hover:bg-blue-500 disabled:opacity-40">{sending ? 'Sending…' : 'Send'}</button>
        </div>
      </div>
    </div>
  );
}
